/**
 * API service for the Review and Publish page
 */
import { GetShiftPlanOutputRequest, GetShiftPlanOutputResponse, ShiftSummaryCategory, ShiftSummaryData, DataEntry } from "./model";
import { API_GATEWAY_PROCESS_NAME, PlanStatus } from "../../utils/constants";
import {
  getUserName,
} from "../../services/boson/bosonClient";
import { apiRequest } from "../../services/apiMiddleware";
import { store } from "../../store";
import { selectStationCode } from "../../store/slices/shiftPlanSlice";
import {
  GET_SHIFT_PLAN_OUTPUT_API,
  PUBLISH_SHIFT_PLAN_API,
  logOperationalMetric,
  MetricType,
  RCC_SHIFT_PLAN_REVIEW_AND_PUBLISH_TAB,
} from "../../utils/metric-utils";

/**
 * Process shift summary data to extract hoursRequired into a separate "hours" category
 * @param response The original shift plan output response
 * @returns Processed response with hours category and hoursRequired removed from original data
 */
const processShiftSummaryData = (response: GetShiftPlanOutputResponse): GetShiftPlanOutputResponse => {
  // Deep clone the response to avoid mutating the original data
  const processedResponse = JSON.parse(JSON.stringify(response));
  
  // Map to store unique combinations of startTime, endTime, and label with their hoursRequired values
  // Key format: "startTime_endTime_label"
  const hoursDataMap = new Map<string, {
    startTime: string;
    endTime: string;
    label: string;
    hoursRequired: number;
  }>();

  // Process each shift summary category
  processedResponse.shiftSummary.forEach((category: ShiftSummaryCategory) => {
    category.planOutput.forEach((planOutput: ShiftSummaryData) => {
      planOutput.data.forEach((dataEntry: DataEntry) => {
        // Check if this entry has hoursRequired
        if (dataEntry.hoursRequired !== undefined) {
          const key = `${planOutput.startTime}_${planOutput.endTime}_${dataEntry.label}`;
          
          // Store the hoursRequired data (latest wins if duplicate)
          hoursDataMap.set(key, {
            startTime: planOutput.startTime,
            endTime: planOutput.endTime,
            label: dataEntry.label,
            hoursRequired: dataEntry.hoursRequired
          });
          
          // Remove hoursRequired from the original entry
          delete dataEntry.hoursRequired;
        }
      });
    });
  });

  // Create the "hours" category if we have any hoursRequired data
  if (hoursDataMap.size > 0) {
    // Group the hours data by time slots
    const timeSlotMap = new Map<string, DataEntry[]>();
    
    hoursDataMap.forEach((hoursData) => {
      const timeSlotKey = `${hoursData.startTime}_${hoursData.endTime}`;
      
      if (!timeSlotMap.has(timeSlotKey)) {
        timeSlotMap.set(timeSlotKey, []);
      }
      
      timeSlotMap.get(timeSlotKey)!.push({
        label: hoursData.label,
        intValue: hoursData.hoursRequired
      });
    });

    // Build the planOutput for the hours category
    const hoursPlanOutput: ShiftSummaryData[] = [];
    timeSlotMap.forEach((dataEntries, timeSlotKey) => {
      const [startTime, endTime] = timeSlotKey.split('_');
      hoursPlanOutput.push({
        startTime,
        endTime,
        data: dataEntries
      });
    });

    // Sort the planOutput by startTime to maintain chronological order
    hoursPlanOutput.sort((a, b) => {
      const timeA = a.startTime.split(':').map(Number);
      const timeB = b.startTime.split(':').map(Number);
      
      // Handle day rollover for night shifts (hours 0-6 are considered next day)
      const hourA = timeA[0] <= 6 ? timeA[0] + 24 : timeA[0];
      const hourB = timeB[0] <= 6 ? timeB[0] + 24 : timeB[0];
      
      if (hourA !== hourB) {
        return hourA - hourB;
      }
      return timeA[1] - timeB[1]; // Compare minutes if hours are equal
    });

    // Add the hours category to the shift summary
    const hoursCategory: ShiftSummaryCategory = {
      heading: "hours",
      planOutput: hoursPlanOutput
    };
    
    processedResponse.shiftSummary.push(hoursCategory);
  }

  return processedResponse;
};

/**
 * Generate new shift plan
 * @returns Promise with success status
 */
export const getShiftPlanOutput = async (
  request: GetShiftPlanOutputRequest
) => {
  const startTime = Date.now();
  const stationCode = selectStationCode(store.getState()) || '';
  const requestPayload = {
    resourcePath: "/getShiftPlanOutput",
    httpMethod: "post",
    processName: API_GATEWAY_PROCESS_NAME,
    requestBody: request
  };
  try {
    const response = await apiRequest(requestPayload);
    logOperationalMetric(startTime, stationCode, RCC_SHIFT_PLAN_REVIEW_AND_PUBLISH_TAB, GET_SHIFT_PLAN_OUTPUT_API, MetricType.SUCCESS);
    return processShiftSummaryData(response.data);
  } catch (error: any) {
    logOperationalMetric(startTime, stationCode, RCC_SHIFT_PLAN_REVIEW_AND_PUBLISH_TAB, GET_SHIFT_PLAN_OUTPUT_API, MetricType.FAILED, error);
    throw error;
  }
};

/**
 * Publish shift plan by updating status to APPROVED
 * @param planId The plan ID to publish
 * @returns Promise indicating success
 */
export const publishShiftPlan = async (planId: string) => {
  const startTime = Date.now();
  const stationCode = selectStationCode(store.getState()) || '';
  const userId: string = (await getUserName()) as string;

  const request = {
    resourcePath: "/publishShiftPlan",
    httpMethod: "PUT",
    processName: API_GATEWAY_PROCESS_NAME,
    requestBody: {
      planId: planId,
      status: PlanStatus.APPROVED,
      updatedBy: userId,
      nodeId: stationCode,
    },
  };

  let metricLogged = false;
  try {
    const response = await apiRequest(request);
    console.log("Shift plan published successfully:", response);
    if (response.status === 500) {
      metricLogged = true;
      logOperationalMetric(startTime, stationCode, RCC_SHIFT_PLAN_REVIEW_AND_PUBLISH_TAB, PUBLISH_SHIFT_PLAN_API, MetricType.FAILED, response);
      throw new Error(response.errorResponse || 'Failed to publish shift plan');
    }
    logOperationalMetric(startTime, stationCode, RCC_SHIFT_PLAN_REVIEW_AND_PUBLISH_TAB, PUBLISH_SHIFT_PLAN_API, MetricType.SUCCESS);
    return response.data;
  } catch (error: any) {
    if (!metricLogged) {
      logOperationalMetric(startTime, stationCode, RCC_SHIFT_PLAN_REVIEW_AND_PUBLISH_TAB, PUBLISH_SHIFT_PLAN_API, MetricType.FAILED, error);
    }
    throw error;
  }
};
